(() => {
  const zeroDecimalCurrencies = new Set(["BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF"]);
  const roundingUnits = {
    CLP: 100,
    GNF: 100,
    HUF: 100,
    IDR: 1000,
    JPY: 100,
    KRW: 1000,
    PYG: 100,
    RWF: 100,
    TWD: 100,
    UGX: 100,
    VND: 1000,
    XAF: 100,
    XOF: 100,
    XPF: 100
  };
  const defaultMoneyTextPattern = /(€\s*(?:99|250)(?:[,.]00)?(?!\d)|(?:99|250)(?:[,.]00)?\s*€|(?:99|250)\s*EUR)/gi;
  // US market (USD fixed prices): worldwide EUR amounts in static copy map to the USD policy ($499 free-shipping threshold, $49 flat rate).
  const usMoneyTextPattern = /(€\s*(?:19|99|250)(?:[,.]00)?(?!\d)|(?:19|99|250)(?:[,.]00)?\s*€|(?:19|99|250)\s*EUR)/gi;
  const usLocalAmounts = { 250: 499, 19: 49 };
  const shippingDestinationPattern = /(전\s*세계\s*무료\s*배송|全世界送料無料|全球免运费|全球免運費|kostenloser\s+weltweiter\s+versand|weltweit\s+kostenloser\s+versand|env[ií]o\s+grat(?:is|uito)\s+a\s+todo\s+el\s+mundo|livraison\s+gratuite\s+dans\s+le\s+monde\s+entier|ingyenes\s+sz[aá]ll[ií]t[aá]s\s+vil[aá]gszerte|spedizione\s+gratuita\s+in\s+tutto\s+il\s+mondo|gratis\s+wereldwijde\s+verzending|free\s+worldwide\s+shipping|worldwide\s+free\s+shipping|worldwide\s+shipping)/i;
  const translations = {
    ko: {
      label: "배송비",
      freeLabel: "무료",
      paidTemplate: "무료배송까지 [amount] 남았어요.",
      freeTemplate: "무료배송이 적용됐어요.",
      localFreeTemplate: "현지 배송은 무료예요."
    },
    ja: {
      label: "配送料",
      freeLabel: "無料",
      paidTemplate: "あと [amount] で送料無料になります。",
      freeTemplate: "送料無料が適用されました。",
      localFreeTemplate: "国内配送は無料です。"
    },
    "zh-tw": {
      label: "運費",
      freeLabel: "免費",
      paidTemplate: "再購買 [amount] 即享免運。",
      freeTemplate: "已享免運。",
      localFreeTemplate: "本地配送免費。"
    },
    "zh-hant": {
      label: "運費",
      freeLabel: "免費",
      paidTemplate: "再購買 [amount] 即享免運。",
      freeTemplate: "已享免運。",
      localFreeTemplate: "本地配送免費。"
    },
    "zh-cn": {
      label: "运费",
      freeLabel: "免费",
      paidTemplate: "再购买 [amount] 即享免运。",
      freeTemplate: "已享免运。",
      localFreeTemplate: "本地配送免费。"
    },
    "zh-hans": {
      label: "运费",
      freeLabel: "免费",
      paidTemplate: "再购买 [amount] 即享免运。",
      freeTemplate: "已享免运。",
      localFreeTemplate: "本地配送免费。"
    },
    de: {
      label: "Versand",
      freeLabel: "Kostenlos",
      paidTemplate: "Noch [amount] bis zum kostenlosen Versand.",
      freeTemplate: "Kostenloser Versand freigeschaltet.",
      localFreeTemplate: "Lokaler Versand ist kostenlos."
    },
    es: {
      label: "Envío",
      freeLabel: "Gratis",
      paidTemplate: "Añade [amount] más para envío gratis.",
      freeTemplate: "Envío gratis desbloqueado.",
      localFreeTemplate: "El envío local es gratis."
    },
    fr: {
      label: "Livraison",
      freeLabel: "Gratuite",
      paidTemplate: "Ajoutez [amount] pour la livraison gratuite.",
      freeTemplate: "Livraison gratuite débloquée.",
      localFreeTemplate: "La livraison locale est gratuite."
    },
    hu: {
      label: "Szállítás",
      freeLabel: "Ingyenes",
      paidTemplate: "Még [amount] az ingyenes szállításig.",
      freeTemplate: "Az ingyenes szállítás elérhető.",
      localFreeTemplate: "A helyi szállítás ingyenes."
    },
    it: {
      label: "Spedizione",
      freeLabel: "Gratis",
      paidTemplate: "Aggiungi [amount] per la spedizione gratuita.",
      freeTemplate: "Spedizione gratuita sbloccata.",
      localFreeTemplate: "La spedizione locale è gratuita."
    },
    nl: {
      label: "Verzending",
      freeLabel: "Gratis",
      paidTemplate: "Voeg [amount] toe voor gratis verzending.",
      freeTemplate: "Gratis verzending vrijgespeeld.",
      localFreeTemplate: "Lokale verzending is gratis."
    }
  };

  const getActiveCurrency = () => window.Shopify?.currency?.active || document.querySelector('meta[id="in-context-paypal-metadata"]')?.dataset.currency || "EUR";

  const getLocaleCode = () => {
    const pathLocale = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2,4})?)(?:\/|$)/i)?.[1];
    return String(pathLocale || document.documentElement.lang || window.Shopify?.locale || "").toLowerCase();
  };

  const getLocaleCopy = () => {
    const locale = getLocaleCode();
    return translations[locale] || translations[locale.split("-")[0]];
  };

  const getCountryCode = () => {
    const code = window.vividifyShippingPolicy?.countryIsoCode || window.Shopify?.country || "";
    return String(code).toUpperCase();
  };

  const getCountryName = () => {
    const countryCode = getCountryCode();
    if (!/^[A-Z]{2}$/.test(countryCode)) return window.vividifyShippingPolicy?.countryName || "";
    const locale = getLocaleCode() || undefined;

    try {
      const displayName = new Intl.DisplayNames(locale ? [locale] : undefined, { type: "region" }).of(countryCode);
      if (displayName) return displayName;
    } catch {
      // Fall back to Shopify's localized country name when Intl.DisplayNames is unavailable.
    }

    return window.vividifyShippingPolicy?.countryName || countryCode;
  };

  const getCurrencyRate = () => {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  };

  const getRoundingUnit = (currency) => {
    if (currency === "EUR") return 0;
    if (roundingUnits[currency]) return roundingUnits[currency];
    return zeroDecimalCurrencies.has(currency) ? 100 : 1;
  };

  const roundForShopify = (amount, currency = getActiveCurrency()) => {
    const unit = getRoundingUnit(currency);
    if (!unit) return amount;
    return Math.ceil((amount - 0.000001) / unit) * unit;
  };

  // Disambiguate currency symbols shared by many currencies ($ family: USD/AUD/CAD/NZD/SGD/HKD/TWD/MXN...,
  // kr family: DKK/SEK/NOK/ISK) by prefixing the ISO code, e.g. "SGD $227.00", "DKK 1.120,00 kr.".
  // Unique symbols (EUR/GBP/JPY/KRW/HUF...) keep their native formatting.
  const ambiguousSymbolPattern = /\$|(?:^|[\s\d.,(])kr\.?(?=[\s\d.,)]|$)/i;
  const clarifyCurrency = (formatted, currency) => {
    if (currency === "EUR" || !ambiguousSymbolPattern.test(formatted)) return formatted;
    if (formatted.toUpperCase().includes(currency)) return formatted;
    return currency + " " + formatted;
  };

  const formatPresentment = (amount, rounded = true) => {
    const currency = getActiveCurrency();
    const displayAmount = rounded ? roundForShopify(amount, currency) : amount;
    const decimals = zeroDecimalCurrencies.has(currency) ? 0 : 2;
    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(displayAmount);
      return clarifyCurrency(formatted, currency);
    } catch {
      return displayAmount.toFixed(decimals) + " " + currency;
    }
  };

  const formatEur = (eurAmount) => formatPresentment(eurAmount * getCurrencyRate());

  const replaceAmountPlaceholder = (element, template, amount) => {
    if (!element) return;
    const parts = String(template || "").split("[amount]");
    element.replaceChildren();
    element.append(document.createTextNode(parts[0] || ""));
    if (parts.length > 1) {
      const amountElement = document.createElement("strong");
      amountElement.className = "vividify-shipping-summary__amount";
      amountElement.textContent = amount;
      element.append(amountElement, document.createTextNode(parts.slice(1).join("[amount]")));
    }
  };

  const isUsMarket = () => getCountryCode() === "US" && getActiveCurrency() === "USD";

  const wrapStaticMoneyText = (root = document) => {
    const scope = root.body || root;
    if (!scope || !document.createTreeWalker) return;
    const moneyTextPattern = isUsMarket() ? usMoneyTextPattern : defaultMoneyTextPattern;
    const textNodes = [];
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest(".vividify-money-convert")) return NodeFilter.FILTER_REJECT;
        if (["SCRIPT", "STYLE", "TEXTAREA", "NOSCRIPT"].includes(parent.tagName)) return NodeFilter.FILTER_REJECT;
        moneyTextPattern.lastIndex = 0;
        return moneyTextPattern.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      node.nodeValue.replace(moneyTextPattern, (match, _matched, index) => {
        fragment.append(document.createTextNode(node.nodeValue.slice(lastIndex, index)));
        const span = document.createElement("span");
        span.className = "vividify-money-convert";
        span.dataset.eur = (match.match(/\d+/) || ["250"])[0];
        span.textContent = match;
        fragment.append(span);
        lastIndex = index + match.length;
      });
      fragment.append(document.createTextNode(node.nodeValue.slice(lastIndex)));
      node.parentNode.replaceChild(fragment, node);
    });
  };

  const formatWholeLocal = (amount) => {
    const currency = getActiveCurrency();
    try {
      return clarifyCurrency(new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount), currency);
    } catch {
      return amount + " " + currency;
    }
  };

  const updateConvertedMoney = (root = document) => {
    const usMarket = isUsMarket();
    root.querySelectorAll(".vividify-money-convert[data-eur]").forEach((element) => {
      const eurAmount = Number(element.dataset.eur);
      if (!Number.isFinite(eurAmount)) return;
      if (usMarket && usLocalAmounts[eurAmount]) {
        element.textContent = formatWholeLocal(usLocalAmounts[eurAmount]);
        return;
      }
      element.textContent = formatEur(eurAmount);
    });
  };

  const getFreeShippingDestinationText = (countryName) => {
    const locale = getLocaleCode();
    const language = locale.split("-")[0];

    if (language === "ko") return `${countryName}까지 무료 배송`;
    if (language === "ja") return `${countryName}への送料無料`;
    if (language === "de") return `Kostenloser Versand (${countryName})`;
    if (language === "es") return `Envío gratuito (${countryName})`;
    if (language === "fr") return `Livraison gratuite (${countryName})`;
    if (language === "hu") return `Ingyenes szállítás (${countryName})`;
    if (language === "it") return `Spedizione gratuita (${countryName})`;
    if (language === "nl") return `Gratis verzending (${countryName})`;
    if (locale.includes("zh-tw") || locale.includes("zh-hant")) return `配送至${countryName}免運費`;
    if (language === "zh") return `配送至${countryName}免运费`;
    return `Free shipping to ${countryName}`;
  };

  const replaceShippingDestination = (text, countryName) => {
    const freeShippingText = getFreeShippingDestinationText(countryName);

    return String(text || "")
      .replace(/전\s*세계\s*무료\s*배송/g, freeShippingText)
      .replace(/全世界送料無料/g, freeShippingText)
      .replace(/全球免运费/g, freeShippingText)
      .replace(/全球免運費/g, freeShippingText)
      .replace(/kostenloser\s+weltweiter\s+versand/gi, freeShippingText)
      .replace(/weltweit\s+kostenloser\s+versand/gi, freeShippingText)
      .replace(/env[ií]o\s+grat(?:is|uito)\s+a\s+todo\s+el\s+mundo/gi, freeShippingText)
      .replace(/livraison\s+gratuite\s+dans\s+le\s+monde\s+entier/gi, freeShippingText)
      .replace(/ingyenes\s+sz[aá]ll[ií]t[aá]s\s+vil[aá]gszerte/gi, freeShippingText)
      .replace(/spedizione\s+gratuita\s+in\s+tutto\s+il\s+mondo/gi, freeShippingText)
      .replace(/gratis\s+wereldwijde\s+verzending/gi, freeShippingText)
      .replace(/free\s+worldwide\s+shipping/gi, freeShippingText)
      .replace(/worldwide\s+free\s+shipping/gi, freeShippingText)
      .replace(/worldwide\s+shipping/gi, `shipping to ${countryName}`);
  };

  const updateShippingDestinationText = (root = document) => {
    const countryName = getCountryName();
    if (!countryName || !document.createTreeWalker) return;

    const scope = root.body || root;
    const textNodes = [];
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest(".vividify-money-convert")) return NodeFilter.FILTER_REJECT;
        if (["SCRIPT", "STYLE", "TEXTAREA", "NOSCRIPT"].includes(parent.tagName)) return NodeFilter.FILTER_REJECT;
        shippingDestinationPattern.lastIndex = 0;
        return shippingDestinationPattern.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const replacement = replaceShippingDestination(node.nodeValue, countryName);
      if (replacement !== node.nodeValue) node.nodeValue = replacement;
    });
  };

  const refreshShippingPolicyText = (root = document) => {
    wrapStaticMoneyText(root);
    updateConvertedMoney(root);
    updateShippingDestinationText(root);
  };

  class VividifyShippingSummary extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    render() {
      const rate = getCurrencyRate();
      const country = (this.dataset.country || "").toUpperCase();
      const cartTotal = Number(this.dataset.cartTotal || 0);
      // data-threshold-local / data-shipping-local: amounts already in the presentment currency (US market, fixed USD prices).
      const threshold = this.dataset.thresholdLocal
        ? Number(this.dataset.thresholdLocal)
        : roundForShopify(Number(this.dataset.thresholdEur || 250) * rate);
      const paidShipping = country === "HU"
        ? 0
        : this.dataset.shippingLocal
          ? Number(this.dataset.shippingLocal)
          : roundForShopify(Number(this.dataset.shippingEur || 19) * rate);
      const localeCopy = getLocaleCopy();
      const labelElement = this.querySelector(".vividify-shipping-summary__label");
      const priceElement = this.querySelector("[data-vividify-shipping-price]");
      const messageElement = this.querySelector("[data-vividify-shipping-message]");
      const progressElement = this.querySelector("[data-vividify-shipping-progress]");
      const progress = threshold > 0 ? Math.max(0, Math.min(100, (cartTotal / threshold) * 100)) : 100;

      if (localeCopy) {
        this.dataset.freeLabel = localeCopy.freeLabel;
        this.dataset.freeTemplate = localeCopy.freeTemplate;
        this.dataset.label = localeCopy.label;
        this.dataset.localFreeTemplate = localeCopy.localFreeTemplate;
        this.dataset.paidTemplate = localeCopy.paidTemplate;
        if (labelElement) labelElement.textContent = localeCopy.label;
      }

      if (paidShipping <= 0) {
        this.dataset.shippingState = "local-free";
        if (progressElement) progressElement.style.width = "100%";
        if (priceElement) priceElement.textContent = this.dataset.freeLabel || "Free";
        if (messageElement) messageElement.textContent = this.dataset.localFreeTemplate || this.dataset.freeTemplate || "Free shipping unlocked.";
        return;
      }

      if (cartTotal >= threshold) {
        this.dataset.shippingState = "free";
        if (progressElement) progressElement.style.width = "100%";
        if (priceElement) priceElement.textContent = this.dataset.freeLabel || "Free";
        if (messageElement) messageElement.textContent = this.dataset.freeTemplate || "Free shipping unlocked.";
        return;
      }

      const remaining = Math.max(0, threshold - cartTotal);
      this.dataset.shippingState = "paid";
      if (progressElement) progressElement.style.width = progress + "%";
      if (priceElement) priceElement.textContent = formatPresentment(paidShipping, false);
      if (messageElement) {
        replaceAmountPlaceholder(messageElement, this.dataset.paidTemplate || "Add [amount] more for free shipping.", formatPresentment(remaining, false));
      }
    }
  }

  if (!customElements.get("vividify-shipping-summary")) {
    customElements.define("vividify-shipping-summary", VividifyShippingSummary);
  }

  refreshShippingPolicyText();
  document.addEventListener("DOMContentLoaded", () => {
    refreshShippingPolicyText();
    window.setTimeout(refreshShippingPolicyText, 500);
    window.setTimeout(refreshShippingPolicyText, 1500);
  });
  document.addEventListener("shopify:section:load", (event) => {
    refreshShippingPolicyText(event.target);
  });
})();
